import { defineStore, storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useHighlights } from '@/stores/highlights/highlights';

export const useNudges = defineStore('nudges', () => {
  const { bodyMessages, inputFields: fields } = storeToRefs(useHighlights());

  const allBlocks = computed(() => {
    const result = [];

    fields.value
      .forEach(({ sections, type }) => {
        sections.forEach(({ blocks }) => {
          blocks.forEach((block) => {
            result.push({ ...block, fieldType: type });
          });
        });
      });

    return result;
  });

  const blockById = computed(() => allBlocks.value.reduce((res, block) => ({
    ...res,
    [block.id]: block,
  }), {}));

  const lastBlockOfEachSection = computed(() => allBlocks.value
    .filter((block) => {
      // filter out empty blocks as there's no way to highlight empty content
      const text = block.html.replace(/<[^>]*>/g, '');
      return !block.lockType && text.length > 3;
    })
    .reduce((res, block) => {
      const { detected_sections: detectedSections } = block;
      (detectedSections || []).forEach((section) => {
        // eslint-disable-next-line no-param-reassign
        res[section] = block;
      });
      return res;
    }, {}));

  const allNudges = computed(() => Object.keys(bodyMessages.value)
    .map((key) => bodyMessages.value[key])
    .filter(({ is_nudge: isNudge }) => isNudge));

  const nudgesByFocusSection = computed(() => allNudges.value.reduce((res, nudge) => {
    const { focus_sections: focusSections = [] } = nudge;
    focusSections.forEach((section) => {
      if (!res[section]) {
        // eslint-disable-next-line no-param-reassign
        res[section] = [];
      }
      res[section].push(nudge);
    });
    return res;
  }, {}));

  const nudgesByBlockId = computed(() => {
    const result = {};

    Object.keys(lastBlockOfEachSection.value)
      .map((section) => [section, lastBlockOfEachSection.value[section]])
      .forEach(([section, block]) => {
        const { id } = block;
        const nudges = nudgesByFocusSection.value[section] || [];
        nudges.forEach((nudge) => {
          if (!result[id]) {
            result[id] = {};
          }
          result[id][nudge.hash] = nudge;
        });
      });

    return result;
  });

  const fieldNudges = computed(() => fields.value
    .map(({ type }) => type)
    .reduce((res, fieldType) => {
      // eslint-disable-next-line no-param-reassign
      res[fieldType] = [];
      Object.keys(nudgesByBlockId.value)
        .map((blockId) => [blockById.value[blockId], nudgesByBlockId.value[blockId]])
        .filter(([block]) => block.fieldType === fieldType)
        .forEach(([block, nudges]) => {
          res[fieldType].push({
            block,
            messages: Object.keys(nudges).map((hash) => ({
              ...nudges[hash],
              id: block.id,
              fieldType,
              className: nudges[hash].severity_class,
            })),
          });
        });
      return res;
    }, {}));

  return {
    allBlocks,
    lastBlockOfEachSection,
    allNudges,
    nudgesByFocusSection,
    nudgesByBlockId,
    fieldNudges,
  };
});
